/**
 * MarketingLayout — public-facing pages (About, Contact, and parent for Legal).
 *
 * Header: logo + wordmark + nav (Pricing / About / Contact) + Sign in CTA (brass).
 * Footer: legal links + sub-processors + © + tagline.
 */

import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ReignMark } from '../reign-mark';

interface MarketingLayoutProps {
  children: ReactNode;
}

export function MarketingLayout({ children }: MarketingLayoutProps) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--ink)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--sans)',
      }}
    >
      <MarketingHeader />
      <main id="main" style={{ flex: 1 }}>
        {children}
      </main>
      <MarketingFooter />
    </div>
  );
}

export function MarketingHeader() {
  return (
    <header
      className="marketing-chrome"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 64,
        borderBottom: '1px solid var(--hairline)',
        background: 'var(--bg)',
        position: 'sticky',
        top: 0,
        zIndex: 40,
      }}
    >
      {/* Logo */}
      <Link
        to="/"
        aria-label="Elreign home"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textDecoration: 'none',
        }}
      >
        <ReignMark size={26} />
        <span className="wordmark" style={{ fontSize: 18 }}>
          Elreign
        </span>
      </Link>

      {/* Nav */}
      {/* Padding and gaps live in design-system.css (.marketing-chrome*) so the
          header can drop its page links at phone widths (design gate F1: the
          wordmark used to collide with "About" at 375 px). */}
      <nav
        className="marketing-chrome-nav"
        style={{ display: 'flex', alignItems: 'center' }}
        aria-label="Main navigation"
      >
        {[
          { label: 'About',   to: '/about'   },
          { label: 'Help',    to: '/help'    },
          { label: 'Contact', to: '/contact' },
        ].map(({ label, to }) => (
          <Link
            key={to}
            to={to as never}
            className="marketing-chrome-link"
            style={{
              fontSize: 14,
              color: 'var(--ink-2)',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            {label}
          </Link>
        ))}

        <Link
          to="/auth/login"
          style={{
            background: 'var(--accent-400)',
            color: 'var(--primary-900)',
            padding: '9px 20px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
            border: 'none',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          Sign in
        </Link>
      </nav>
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer
      style={{
        borderTop: '1px solid var(--hairline)',
        padding: '32px 48px 28px',
        background: 'var(--bg-2)',
      }}
    >
      <div
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* Upper row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 24,
          }}
        >
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <ReignMark size={22} />
              <span className="wordmark" style={{ fontSize: 16 }}>
                Elreign
              </span>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0, maxWidth: 280, lineHeight: 1.55 }}>
              {/* 2026-08-23 tagline (Paul) — see components/layouts/auth-layout.tsx. */}
              Buy back your time. Simplify your trust administration.
              Built for South African fiduciary firms.
            </p>
          </div>

          {/* Legal links */}
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            <FooterLinkGroup
              label="Legal"
              links={[
                { label: 'Privacy policy',          to: '/legal/privacy' },
                { label: 'Terms of service',        to: '/legal/terms' },
                { label: 'Cookie policy',           to: '/legal/cookies' },
                { label: 'PAIA manual',             to: '/legal/paia' },
              ]}
            />
            <FooterLinkGroup
              label="Trust"
              links={[
                { label: 'Trust centre',            to: '/legal/trust-centre' },
                { label: 'Security disclosure',     to: '/legal/security' },
                { label: 'Sub-processors',          to: '/legal/subprocessors' },
              ]}
            />
            <FooterLinkGroup
              label="Company"
              links={[
                { label: 'About',     to: '/about' },
                { label: 'Help',      to: '/help' },
                { label: 'Contact',   to: '/contact' },
              ]}
            />
          </div>
        </div>

        {/* Lower row */}
        <div
          style={{
            borderTop: '1px solid var(--hairline)',
            paddingTop: 18,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            &copy; 2026 Elreign Legacy (Pty) Ltd. All rights reserved.
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            Registered in South Africa. POPIA-aligned.
          </span>
        </div>
      </div>
    </footer>
  );
}

interface FooterLinkGroupProps {
  label: string;
  links: Array<{ label: string; to: string }>;
}

function FooterLinkGroup({ label, links }: FooterLinkGroupProps) {
  return (
    <div>
      {/* BEH-27, round 2 (2026-09-22): the bare `.eyebrow` class defaults to
          --chip-accent (brand gold), fine for an app chip, wrong for a footer
          column header that renders on every route through this ONE shared
          footer (`/product`, `/about`, `/help`, etc. — anywhere the page
          reaches for <MarketingLayout>). "Gold means hero, nowhere else" —
          this footer never sits in a hero, so `.ink` (the same modifier
          `.eyebrow.ink` already carries elsewhere for a light-background
          label) is the fix here, not a new page-scoped CSS rule. */}
      <div className="eyebrow ink" style={{ marginBottom: 12 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {links.map(({ label: lLabel, to }) => (
          <Link
            key={to}
            to={to as never}
            style={{ fontSize: 13, color: 'var(--ink-3)', textDecoration: 'none' }}
          >
            {lLabel}
          </Link>
        ))}
      </div>
    </div>
  );
}
